<template>
  <modal @before-close="listenEvent" :title="$t('settings.title')" class="settings" name="settings" :height="300">
    <template slot="container">
      <div class="settings__section" v-if="$route.name !== 'analytics'">
        <div class="settings__section__title">
          {{ $t('settings.markerTiny.title') }}
        </div>
        <div class="settings__section__container">
          <ui-switch
            id="settings-marker-tiny"
            class="settings__section__container__switch"
            :value="settings.marker.tiny"
            @input="onClickMarkerSize"
          >
            <span class="settings__section__container__switch__description">
              {{ $t('settings.markerTiny.description') }}
            </span>
            <span class="settings__section__container__switch__caption" v-html="$t('settings.markerTiny.caption')">
            </span>
          </ui-switch>
        </div>
      </div>
      <div class="settings__section" v-if="!isIntentionView && $route.name !== 'analytics'">
        <div class="settings__section__title">
          {{ $t('settings.markerScore.title') }}
        </div>
        <div class="settings__section__container">
          <ui-switch
            id="settings-marker-score"
            class="settings__section__container__switch"
            :value="settings.marker.score"
            @input="onClickMarkerScore"
          >
            <span class="settings__section__container__switch__description">
              {{ $t('settings.markerScore.description') }}
            </span>
            <span class="settings__section__container__switch__caption" v-html="$t('settings.markerScore.caption')">
            </span>
          </ui-switch>
        </div>
      </div>
      <div class="settings__section" v-if="$route.name !== 'analytics'">
        <div class="settings__section__title">
          {{ $t('settings.markerAll.title') }}
        </div>
        <div class="settings__section__container">
          <ui-switch
            id="settings-marker-all"
            class="settings__section__container__switch"
            :value="settings.marker.all"
            @input="onClickUpdateMap"
          >
            <span class="settings__section__container__switch__description">
              {{ $t('settings.markerAll.description') }}
            </span>
            <span
              class="settings__section__container__switch__caption settings__section__container__switch__caption--warning"
              v-html="$t('settings.markerAll.caption')"
            >
            </span>
          </ui-switch>
        </div>
      </div>
      <div class="settings__section" v-if="!isIntentionView && $route.name !== 'analytics'">
        <div class="settings__section__title">
          {{ $t('settings.markerAcronym.title') }}
        </div>
        <div class="settings__section__container">
          <ui-switch
            id="settings-marker-acronym"
            class="settings__section__container__switch"
            :value="settings.marker.acronym"
            @input="onClickToggleMarkerAcronym"
          >
            <span class="settings__section__container__switch__description">
              {{ $t('settings.markerAcronym.description') }}
            </span>
          </ui-switch>
        </div>
      </div>
      <div class="settings__section">
        <div class="settings__section__title">
          {{ $t('settings.share.title') }}
        </div>
        <div class="settings__section__container">
          <div class="settings__section__container__description">
            {{ $t('settings.share.description') }}
          </div>
          <ui-button
            class="settings__section__container__button"
            :label="$t('settings.share.button')"
            button="cta"
            variant="gtr"
            @click="onClickCopyToClipBoard"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UiSwitch from '@/components/UI/Switch.vue'
import UiButton from '@/components/UI/Button.vue'
import {
  buildFilters,
  buildSource,
  buildGroups,
  buildDate,
  buildKpi,
  buildDistributionForSharing,
  buildBrandsForSharing,
  buildLocale,
  buildLevel,
  buildCompetitor,
  buildLocation,
  buildCountries,
  buildView,
} from '@/utils/urlParams.util'
import { notification } from '@/utils/notification.util'
import { copyToClipboard } from '@/utils/extractor.util'
import { launchGAEvent } from '@/utils/gtm.util'

export default {
  name: 'GmapSettings',
  components: {
    UiSwitch,
    UiButton,
  },
  computed: {
    ...mapState({
      settings: state => state.radar.settings,
      filters: state => state.radar.filters,

      refine: state => state.refine,
      currentKpi: state => state.refine.currentKpi,
      currentTimeline: state => state.refine.currentTimeline,

      brandBrands: state => state.data.brands,
      brandsGroup: state => state.data.brandsGroup,
    }),
    ...mapGetters({
      isIntentionView: 'radar/isIntentionView',
    }),
  },
  methods: {
    ...mapActions({
      toggleMarkerSize: 'radar/toggleMarkerSize',
      toggleMarkerScore: 'radar/toggleMarkerScore',
      toggleMarkerAll: 'radar/toggleMarkerAll',
      toggleMarkerAcronym: 'radar/toggleMarkerAcronym',
    }),
    updateMap(bool) {
      this.toggleMarkerAll(bool)
      this.$emit('updateMapOptions', bool)
    },
    copyToClipBoard() {
      const query = this.$route.query
      let urlParams = '?'
      urlParams += buildLocale(query.locale)
      urlParams += buildLevel(query.level)
      urlParams += buildCompetitor(query.competitor)
      urlParams += buildLocation(null, this.refine.currentDistance)
      urlParams += buildSource(this.refine.currentSource)
      urlParams += buildKpi(this.refine.currentKpi)
      urlParams += buildDistributionForSharing(this.refine.currentDistribution)
      urlParams += buildBrandsForSharing(this.refine.activeBrands, this.brandBrands[this.currentKpi].brands)
      urlParams += buildGroups(this.refine.activeGroups)
      urlParams += buildFilters(this.refine.activeFilters)
      urlParams += buildCountries(this.refine.currentCountries)
      urlParams += buildView(query.view)
      urlParams += buildDate(this.currentTimeline)
      urlParams += this.settings.marker.score ? `&marker_score=1` : ''
      urlParams += this.settings.marker.tiny ? `&marker_tiny=1` : ''
      urlParams += this.settings.marker.acronym ? `&marker_acronym=1` : ''
      urlParams += this.settings.marker.all ? `&marker_all=1` : ''
      const sharingUrl = `${window.location.origin}${window.location.pathname}${urlParams}`
      copyToClipboard(sharingUrl)
      notification({
        text: this.$t('settings.notification.share'),
        type: 'success',
      })
    },
    listenEvent() {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_settings`, 'hide')
    },
    onClickMarkerSize(value) {
      const state = value ? 'on' : 'off'
      launchGAEvent(this.$route.params.id, 'markers_settings', `markers_size_${state}`)
      this.toggleMarkerSize(value)
    },
    onClickMarkerScore(value) {
      const state = value ? 'on' : 'off'
      launchGAEvent(this.$route.params.id, 'markers_settings', `markers_score_${state}`)
      this.toggleMarkerScore(value)
    },
    onClickUpdateMap(value) {
      const state = value ? 'on' : 'off'
      launchGAEvent(this.$route.params.id, 'markers_settings', `display_all_${state}`)
      this.updateMap(value)
    },
    onClickToggleMarkerAcronym(value) {
      const state = value ? 'on' : 'off'
      launchGAEvent(this.$route.params.id, 'markers_settings', `brand_acronym_${state}`)
      this.toggleMarkerAcronym(value)
    },
    onClickCopyToClipBoard() {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_settings`, 'generate_link')
      this.copyToClipBoard()
    },
  },
}
</script>

<style lang="scss" scoped>
.settings {
  &__section {
    margin-top: $spacing-sm;

    @media (min-width: $screen-sm) {
      margin-top: $spacing-md;
    }

    &:first-child {
      margin-top: 0;
    }
    &__title {
      @include sub-title;
    }

    &__container {
      &__description {
        margin-bottom: $spacing-sm;
      }

      &__switch {
        &__description,
        &__caption {
          display: block;
        }

        &__caption {
          font-size: $font-size-sm;

          &--warning {
            color: map-get($generic-color-variants, 'error');
          }
        }
      }
    }
  }
}
</style>
